import * as React from 'react';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faMemo } from '@fortawesome/sharp-regular-svg-icons';
import {
    FaOptionIcon,
    editIcon,
    userIcon as contactIcon,
    companyIcon,
} from '@europrocurement/l2d-icons';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useContextMenu } from '@europrocurement/l2d-hooks';
import { useL2DTheme } from '@europrocurement/flexy-components/hooks';
import { ActivityProps, ActionTypeProps } from '../../../types/ActivityHistoryTypes';
import { DisplayFile } from '../../atoms';
import { NoteContent } from '../../atoms/NoteContent/NoteContent';
import { useNotePermissions } from '../../../hooks/useNotePermissions';
import { CustomAvatar } from '../CustomAvatar';
import { NoteToolbox } from '../NoteToolbox/NoteToolbox';
import { NoteEditedIndicator } from '../NoteEditedIndicator/NoteEditedIndicator';
import { NoteContextMenu } from '../ContactInformations/fragments/NoteContextMenu';

export type ActivityTimelineItemProps = {
    activity: ActivityProps;
    isLast: boolean;
    showLogData?: boolean;
    onEditNote?: (
        noteId: string,
        content: string,
        highlighted: boolean,
        memoClient: boolean,
    ) => void;
    onDeleteNote?: (noteId: string) => void;
};

const getActionIcon = (actionType: ActionTypeProps) => {
    switch (actionType) {
        case 'edit':
            return editIcon;
        case 'create_contact':
            return contactIcon;
        case 'create_company':
            return companyIcon;
        default:
            return editIcon;
    }
};

export const ActivityTimelineItem: React.FunctionComponent<ActivityTimelineItemProps> = function ({
    activity,
    isLast,
    showLogData = true,
    onEditNote,
    onDeleteNote,
}) {
    const { palette } = useL2DTheme();
    const [isHovered, setIsHovered] = React.useState(false);
    const [isDataExpanded, setIsDataExpanded] = React.useState(false);
    const noteContentRef = React.useRef<HTMLDivElement>(null);
    const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();

    const notePermissions = useNotePermissions(
        activity.type === 'note' ? activity.userId || '' : '',
    );

    const shouldShowToolbox =
        activity.type === 'note' &&
        isHovered &&
        notePermissions.hasAnyPermission &&
        !!(onEditNote || onDeleteNote) &&
        !contextMenu.open;

    const isNoteEdited =
        activity.type === 'note' &&
        activity.updatedAt &&
        activity.updatedAt.getTime() !== activity.createdAt.getTime();

    const handleEditNote = () => {
        if (activity.type === 'note' && onEditNote) {
            onEditNote(
                activity.id,
                activity.content || '',
                activity.noteType === 'important',
                activity.memoClient,
            );
        }
    };

    const handleDeleteNote = () => {
        if (activity.type === 'note' && onDeleteNote) {
            onDeleteNote(activity.id);
        }
    };

    const handleNoteContextMenu = (event: React.MouseEvent) => {
        if (activity.type === 'note' && notePermissions.hasAnyPermission) {
            openContextMenu(event);
        }
    };

    const handleItemClick = () => {
        if (contextMenu.open) {
            closeContextMenu();
        }
    };

    const noteToken =
        activity.type === 'note' && activity.noteType === 'important'
            ? palette.note?.important
            : palette.note?.default;

    const timeAgo = formatDistanceToNow(activity.createdAt, {
        addSuffix: true,
        locale: fr,
    });

    const userName = activity.userLastName
        ? `${activity.userFirstName} ${activity.userLastName}`
        : activity.userFirstName;

    return (
        <>
            <Box
                sx={{ display: 'flex', position: 'relative', minHeight: '60px' }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleItemClick}
            >
                {!isLast && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: '20px',
                            top: '40px',
                            bottom: '-20px',
                            width: '2px',
                            backgroundColor: palette.divider,
                            zIndex: 0,
                        }}
                    />
                )}

                <Box sx={{ marginRight: '16px', zIndex: 1 }}>
                    {activity.type === 'note' ? (
                        <CustomAvatar
                            firstName={activity.userFirstName}
                            lastName={activity.userLastName || 'Inconnu'}
                            keycloakId={activity.userId}
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '40px',
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                backgroundColor:
                                    palette.activityTimelineItem?.actionIcon?.backgroundColor,
                                color: palette.activityTimelineItem?.actionIcon?.textColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `2px solid ${palette.background.default}`,
                            }}
                        >
                            <FaOptionIcon
                                {...getActionIcon(activity.actionType).props}
                                sx={{
                                    fontSize: '8px',
                                    color: palette.activityTimelineItem?.actionIcon?.textColor,
                                }}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ flex: 1, paddingBottom: '20px' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 0.5,
                        }}
                    >
                        <Typography
                            variant="l2dbodys"
                            sx={{
                                color: palette.text.secondary,
                                marginBottom: '8px',
                                fontWeight: 600,
                            }}
                        >
                            {userName},
                        </Typography>
                        <Typography
                            variant="l2dbodyxs"
                            sx={{
                                color: palette.text.secondary,
                                marginBottom: '8px',
                                fontWeight: 500,
                            }}
                        >
                            {timeAgo}
                        </Typography>
                    </Box>

                    {activity.type === 'note' ? (
                        <>
                            <Box
                                ref={noteContentRef}
                                onContextMenu={handleNoteContextMenu}
                                sx={{
                                    padding: '12px 16px',
                                    backgroundColor: contextMenu.open
                                        ? noteToken?.backgroundColorOnHover
                                        : noteToken?.backgroundColor,
                                    border:
                                        activity.noteType === 'important'
                                            ? `1px solid ${palette.background.default}`
                                            : 'none',
                                    borderRadius: '8px',
                                    marginBottom: activity.attachment ? '12px' : 0,
                                    boxShadow: 'none',
                                    position: 'relative',
                                    width: 'fit-content',
                                    paddingBottom: isNoteEdited ? '24px' : '12px',
                                }}
                            >
                                {activity.memoClient && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '-6px',
                                            left: '-6px',
                                            backgroundColor: palette.background.default,
                                            borderRadius: '50%',
                                            padding: '5px',
                                            border: `1px solid ${palette.primary.main}`,
                                            fontSize: '10px',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faMemo} />
                                    </Box>
                                )}
                                <NoteContent
                                    content={activity.content || ''}
                                    sx={{
                                        color: palette.text.primary,
                                    }}
                                />

                                {isNoteEdited && (
                                    <NoteEditedIndicator
                                        updatedAt={activity.updatedAt!}
                                        updatedByFirstName={activity.updatedByFirstName!}
                                        updatedByLastName={activity.updatedByLastName!}
                                    />
                                )}

                                <NoteToolbox
                                    visible={shouldShowToolbox}
                                    onEdit={handleEditNote}
                                    onDelete={handleDeleteNote}
                                    parentRef={noteContentRef}
                                />
                            </Box>

                            {activity.attachment && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'left',
                                        alignItems: 'center',
                                    }}
                                >
                                    <DisplayFile
                                        nom="Informations juridiques"
                                        styleSize="small"
                                        size={2150000}
                                        mimetype="application/pdf"
                                        mode="card"
                                        color={palette.primary.main}
                                        backgroundColor={palette.primary.light}
                                    />
                                    <DisplayFile
                                        nom="Capture d'écran"
                                        styleSize="small"
                                        size={46254}
                                        mimetype="image/jpeg"
                                        mode="card"
                                        color={palette.primary.main}
                                        backgroundColor={palette.primary.light}
                                    />
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box>
                            <Typography
                                variant="caption"
                                sx={{ color: palette.text.primary }}
                            >
                                {activity.description}
                            </Typography>
                            {showLogData && activity.logData && (
                                <Box sx={{ mt: 0.5 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            gap: 0.5,
                                        }}
                                        onClick={() => setIsDataExpanded((prev) => !prev)}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: palette.text.secondary,
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            Données
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            sx={{ p: 0 }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faChevronDown}
                                                style={{
                                                    fontSize: '10px',
                                                    transition: 'transform 0.2s',
                                                    transform: isDataExpanded
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0deg)',
                                                    color: palette.text.secondary,
                                                }}
                                            />
                                        </IconButton>
                                    </Box>
                                    <Collapse in={isDataExpanded}>
                                        <Box
                                            component="pre"
                                            sx={{
                                                mt: 0.5,
                                                p: 1,
                                                borderRadius: '4px',
                                                backgroundColor: palette.action?.hover,
                                                fontSize: '11px',
                                                overflowX: 'auto',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all',
                                                color: palette.text.primary,
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                            }}
                                        >
                                            {(() => {
                                                try {
                                                    return JSON.stringify(
                                                        JSON.parse(activity.logData),
                                                        null,
                                                        2,
                                                    );
                                                } catch {
                                                    return activity.logData;
                                                }
                                            })()}
                                        </Box>
                                    </Collapse>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {activity.type === 'note' && (
                <NoteContextMenu
                    contextMenu={contextMenu}
                    onClose={closeContextMenu}
                    onEdit={handleEditNote}
                    onDelete={handleDeleteNote}
                    canEdit={notePermissions.canEdit}
                    canDelete={notePermissions.canDelete}
                    noteContent={activity.content || ''}
                    createdBy={userName}
                    createdAt={activity.createdAt}
                    isImportant={activity.noteType === 'important'}
                />
            )}
        </>
    );
};
